import React, { useState } from "react";
import {
  Database,
  Users,
  Map,
  Activity,
  FileText,
  ChevronDown,
  ChevronRight,
  Building,
  User,
  Link,
  Clock,
  MapPin,
  FileSearch,
  Edit2,
  ArrowUpDown,
  Mic,
  Radio,
  List,
  FileAudio,
  MessageSquare,
  Palette,
  Search,
  BarChart,
  BarChart3,
  Package,
  ShoppingCart,
  Shield,
  LogOut,
  UserCheck,
  Workflow,
  Play,
  Copy,
  Github,
  Upload,
  AlertCircle,
  HardDrive,
  ArrowUpCircle,
  Bot,
  LayoutDashboard,
  Server,
  FolderOpen,
  Brain,
  Layout,
  Calendar,
  Key,
  ThumbsUp,
  Rocket,
  CreditCard,
  Zap,
  ScrollText,
  Bell,
  ClipboardList,
  Send,
  AlertTriangle,
  Settings as SettingsIcon,
  Globe,
  BookOpen,
  Megaphone,
} from "lucide-react";
import { SECTIONS, SECTION_PERMISSIONS } from "./constants";
import { useAuth } from "./contexts/AuthContext";
import LoginPage from "./components/auth/LoginPage";

// Import all page components
import ClientManagement from "./components/clients/ClientManagement";
import UserManagement from "./components/users/UserManagement";
import ClientToUser from "./components/client-management/ClientToUser";
import ClientSessions from "./components/client-management/ClientSessions";
import ProductManagement from "./components/client-management/ProductManagement";
import OrderManagement from "./components/client-management/OrderManagement";
import FacilityServiceManagement from "./components/client-management/FacilityServiceManagement";
import UserServiceAccessManagement from "./components/client-management/UserServiceAccessManagement";
import FacilityUserManagement from "./components/client-management/FacilityUserManagement";
import FacilityManagement from "./components/client-management/FacilityManagement";
import FacilityProvisioning from "./components/client-management/FacilityProvisioning";
import ClientOnboardingWizard from "./components/client-management/ClientOnboardingWizard";
import TelegramBotConfig from "./components/telegram/TelegramBotConfig";
import TelegramViolations from "./components/telegram/TelegramViolations";
import DataMapManagement from "./components/datamaps/DataMapManagement";
import IndicatorManagement from "./components/indicators/IndicatorManagement";
import ResultManagement from "./components/results/ResultManagement";
import VectorManager from "./components/vector-management/VectorManager";
import QueryPinecone from "./components/vector-management/QueryPinecone";
import DataQualityDashboard from "./components/vector-management/DataQualityDashboard";
import PodcastManagement from "./components/podcast/PodcastManagement";
import EpisodeManagement from "./components/podcast/EpisodeManagement";
import HostManagement from "./components/podcast/HostManagement";
import StyleManagement from "./components/podcast/StyleManagement";
import EpisodeDetails from "./components/podcast/EpisodeDetails";
import PromptManagement from "./components/podcast/PromptManagement";
import ActiveWorkflows from "./components/n8n/ActiveWorkflows";
import CloneWorkflow from "./components/n8n/CloneWorkflow";
import BackupManager from "./components/n8n/BackupManager";
import UpgradeN8N from "./components/n8n/UpgradeN8N";
import PushToGitHub from "./components/github/PushToGitHub";
import MyIssues from "./components/github/MyIssues";
import PLMChatbot from "./components/plm/PLMChatbot";
import RAGDocumentManager from "./components/rag/RAGDocumentManager";
import KBReviewQueue from "./components/rag/KBReviewQueue";
import GapTasksSteward from "./components/rag/GapTasksSteward";
import RAGChatbot from "./components/rag/RAGChatbot";
import SFTPManagement from "./components/infrastructure/SFTPManagement";
import PlatformDashboard from "./components/dashboard/PlatformDashboard";
import ConversationAnalytics from "./components/analytics/ConversationAnalytics";
import UsageDashboard from "./components/analytics/UsageDashboard";
import FeedbackDashboard from "./components/analytics/FeedbackDashboard";
import ScheduledBriefings from "./components/knowledge-base/ScheduledBriefings";
import CalendarIntegration from "./components/knowledge-base/CalendarIntegration";
import KBTemplates from "./components/knowledge-base/KBTemplates";
import APIAccess from "./components/knowledge-base/APIAccess";
import ChannelManagement from "./components/knowledge-base/ChannelManagement";
import MetadataSchemaManager from "./components/rag/MetadataSchemaManager";
import FederationSettings from "./components/federation/FederationSettings";
import TopicTaxonomy from "./components/federation/TopicTaxonomy";
import EventsAdmin from "./components/federation/EventsAdmin";
import BillingManagement from "./components/billing/BillingManagement";
import InferenceTaskPrompts from "./components/marketing/InferenceTaskPrompts";
import WebhookManagement from "./components/operations/WebhookManagement";
import LogViewer from "./components/operations/LogViewer";
import SystemMessages from "./components/operations/SystemMessages";
import SurveysAdmin from "./components/surveys/SurveysAdmin";
import BroadcastComposer from "./components/operations/BroadcastComposer";
import PermitSources from "./components/construction/PermitSources";
import ConversationHistory from "./components/portal/ConversationHistory";
import ClientDashboard from "./components/portal/ClientDashboard";
import DocumentViewer from "./components/portal/DocumentViewer";
import AccountSettings from "./components/portal/AccountSettings";
import ClientBillingView from "./components/portal/ClientBillingView";

// Icon mapping
const iconComponents = {
  Database,
  Users,
  Map,
  Activity,
  FileText,
  Building,
  User,
  Link,
  Clock,
  MapPin,
  FileSearch,
  Edit2,
  ArrowUpDown,
  Mic,
  Radio,
  List,
  FileAudio,
  MessageSquare,
  Palette,
  Search,
  BarChart,
  Package,
  ShoppingCart,
  Shield,
  UserCheck,
  Workflow,
  Play,
  Copy,
  Github,
  Upload,
  AlertCircle,
  HardDrive,
  ArrowUpCircle,
  Bot,
  LayoutDashboard,
  Server,
  FolderOpen,
  Brain,
  Layout,
  Calendar,
  Key,
  ThumbsUp,
  BarChart3,
  Rocket,
  CreditCard,
  Zap,
  ScrollText,
  Bell,
  ClipboardList,
  Send,
  AlertTriangle,
  Settings: SettingsIcon,
  Globe,
  BookOpen,
  Megaphone,
};

function App() {
  const { isAuthenticated, loading, user, logout, facilityRoles } = useAuth();
  const [currentPage, setCurrentPage] = useState(() => {
    // Admins land on platform dashboard; all others land on their first accessible page
    if (!user || user.role === "admin") return "dashboard";
    const allowed = SECTION_PERMISSIONS;
    for (const section of SECTIONS) {
      const perms = allowed[section.id];
      if (!perms || perms.includes(user.role)) {
        return section.pages[0]?.id || "dashboard";
      }
    }
    return "dashboard";
  });
  const [expandedSections, setExpandedSections] = useState(() => {
    if (!user || user.role === "admin") return ["overview"];
    // Expand the section containing the initial page
    for (const section of SECTIONS) {
      const perms = SECTION_PERMISSIONS[section.id];
      if (!perms || perms.includes(user.role)) return [section.id];
    }
    return [];
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId],
    );
  };

  // Filter sections based on user role and facility-level roles.
  // Permission entries starting with "facility:" match against the user's
  // facilityRoles array (populated from user_service_access.facility_roles).
  const visibleSections = SECTIONS.filter((section) => {
    const allowed = SECTION_PERMISSIONS[section.id];
    if (!allowed) return true; // No restriction = visible to all authenticated users
    return allowed.some((perm) => {
      if (perm.startsWith("facility:")) {
        return facilityRoles.includes(perm.slice(9));
      }
      return perm === user.role;
    });
  });

  const renderPage = () => {
    switch (currentPage) {
      case "dashboard":
        return <PlatformDashboard />;
      case "client-onboarding":
        return <ClientOnboardingWizard onNavigate={setCurrentPage} />;
      case "facilities":
        return <FacilityManagement onNavigate={setCurrentPage} />;
      case "clients":
        return <ClientManagement />;
      case "users":
        return <UserManagement />;
      case "client-to-user":
        return <ClientToUser />;
      case "client-sessions":
        return <ClientSessions />;
      case "products":
        return <ProductManagement />;
      case "orders":
        return <OrderManagement />;
      case "facility-services":
        return <FacilityServiceManagement />;
      case "facility-users":
        return <FacilityUserManagement />;
      case "service-access":
        return <UserServiceAccessManagement />;
      case "datamaps":
        return <DataMapManagement />;
      case "indicators":
        return <IndicatorManagement />;
      case "results":
        return <ResultManagement />;
      case "vector-manager":
        return <VectorManager />;
      case "query-pinecone":
        return <QueryPinecone />;
      case "data-quality":
        return <DataQualityDashboard />;
      case "podcasts":
        return <PodcastManagement />;
      case "episodes":
        return <EpisodeManagement />;
      case "hosts":
        return <HostManagement />;
      case "styles":
        return <StyleManagement />;
      case "prompts":
        return <PromptManagement />;
      case "episode-details":
        return <EpisodeDetails />;
      case "active-workflows":
        return <ActiveWorkflows />;
      case "clone-workflow":
        return <CloneWorkflow />;
      case "backup":
        return <BackupManager />;
      case "upgrade":
        return <UpgradeN8N />;
      case "rag-documents":
        return <RAGDocumentManager />;
      case "kb-review-queue":
        return <KBReviewQueue />;
      case "gap-tasks":
        return <GapTasksSteward />;
      case "rag-chatbot":
        return <RAGChatbot />;
      case "plm-chatbot":
        return <PLMChatbot />;
      case "sftp-management":
        return <SFTPManagement />;
      case "conversation-analytics":
        return <ConversationAnalytics />;
      case "usage-dashboard":
        return <UsageDashboard />;
      case "feedback-dashboard":
        return <FeedbackDashboard />;
      case "scheduled-briefings":
        return <ScheduledBriefings />;
      case "calendar-integration":
        return <CalendarIntegration />;
      case "kb-templates":
        return <KBTemplates />;
      case "api-access":
        return <APIAccess />;
      case "channels":
        return <ChannelManagement />;
      case "metadata-schema":
        return <MetadataSchemaManager />;
      case "federation-settings":
        return <FederationSettings />;
      case "topic-taxonomy":
        return <TopicTaxonomy />;
      case "events-admin":
        return <EventsAdmin />;
      case "provision":
        return <FacilityProvisioning />;
      case "telegram-config":
        return <TelegramBotConfig />;
      case "telegram-violations":
        return <TelegramViolations />;
      case "billing-management":
        return <BillingManagement />;
      case "my-conversations":
        return <ConversationHistory />;
      case "my-usage":
        return <ClientDashboard />;
      case "my-documents":
        return <DocumentViewer />;
      case "my-billing":
        return <ClientBillingView />;
      case "my-settings":
        return <AccountSettings />;
      case "system-messages":
        return <SystemMessages />;
      case "surveys":
        return <SurveysAdmin />;
      case "inference-prompts":
        return <InferenceTaskPrompts />;
      case "broadcast-composer":
        return <BroadcastComposer />;
      case "permit-sources":
        return <PermitSources />;
      case "webhook-management":
        return <WebhookManagement />;
      case "log-viewer":
        return <LogViewer />;
      case "push-to-github":
        return <PushToGitHub />;
      case "my-issues":
        return <MyIssues />;
      default:
        return <PlatformDashboard />;
    }
  };

  const getCurrentPageName = () => {
    for (const section of SECTIONS) {
      const page = section.pages.find((p) => p.id === currentPage);
      if (page) return page.name;
    }
    return "Dashboard";
  };

  return (
    <div className="min-h-screen bg-slate-100 flex">
      <aside className="w-64 bg-white shadow-lg border-r border-slate-200 flex flex-col">
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center">
            <img
              src="/logo-white.png"
              alt="Hyve Applied Intelligence"
              className="h-9"
              style={{ filter: "brightness(0) invert(20%)" }}
            />
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4">
          {visibleSections.map((section) => {
            const SectionIcon = iconComponents[section.icon];
            const isExpanded = expandedSections.includes(section.id);

            return (
              <div key={section.id} className="mb-2">
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full px-4 py-2 flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center">
                    <SectionIcon className="w-5 h-5 text-slate-600 mr-3" />
                    <span className="text-sm font-semibold text-slate-700">
                      {section.name}
                    </span>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  )}
                </button>

                {isExpanded && (
                  <div className="ml-4 mt-1 space-y-1">
                    {section.pages.map((page) => {
                      const PageIcon = iconComponents[page.icon];
                      const isActive = currentPage === page.id;

                      return (
                        <button
                          key={page.id}
                          onClick={() => setCurrentPage(page.id)}
                          className={`w-full px-4 py-2 flex items-center text-sm rounded-lg transition-colors ${
                            isActive
                              ? "bg-blue-50 text-blue-700 font-medium"
                              : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                          }`}
                        >
                          <PageIcon className="w-4 h-4 mr-3" />
                          {page.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-700 truncate">
                {user.first_name} {user.last_name}
              </p>
              <p className="text-xs text-slate-400 truncate">{user.email}</p>
              <p className="text-xs text-blue-600 capitalize">{user.role}</p>
            </div>
            <button
              onClick={logout}
              className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 flex flex-col">
        <header className="bg-white shadow-sm border-b border-slate-200">
          <div className="px-8 py-4">
            <h2 className="text-2xl font-bold text-slate-900">
              {getCurrentPageName()}
            </h2>
          </div>
        </header>

        <div className="flex-1 overflow-auto">
          <div className="max-w-[1600px] mx-auto px-8 py-8">{renderPage()}</div>
        </div>
      </main>
    </div>
  );
}

export default App;
